import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

// Source of record for this app. The Studio view and the Marketplace page render
// the same incident dashboards; this is the canonical customer-facing surface.
const MARKETPLACE_URL = 'https://www.kromarketplace.com/customer-service';

/* ─────────────────────────── Reference data ─────────────────────────── */

const EXCHANGE_NETWORKS = [
  { id: 'institutional', name: 'Institutional Exchange', icon: 'bi-bank2',        tone: 'primary' },
  { id: 'distribution',  name: 'Distribution Channel',   icon: 'bi-diagram-3',    tone: 'info' },
  { id: 'supplier',      name: 'Supplier Network',       icon: 'bi-truck',        tone: 'success' },
];

const SEVERITIES = [
  { id: 'P1', label: 'P1 · Critical', tone: 'danger',    respondMins: 15,  resolveHrs: 4,  desc: 'Settlement or payment path blocked for a participant.' },
  { id: 'P2', label: 'P2 · High',     tone: 'warning',   respondMins: 60,  resolveHrs: 12, desc: 'Degraded function with a workaround available.' },
  { id: 'P3', label: 'P3 · Medium',   tone: 'info',      respondMins: 240, resolveHrs: 48, desc: 'Non-blocking defect or data question.' },
  { id: 'P4', label: 'P4 · Low',      tone: 'secondary', respondMins: 480, resolveHrs: 96, desc: 'Request for information or enhancement.' },
];

const STATUS_META = {
  OPEN:              { label: 'Open',              badge: 'danger',    icon: 'bi-record-circle' },
  TRIAGED:           { label: 'Triaged',           badge: 'warning',   icon: 'bi-funnel' },
  IN_PROGRESS:       { label: 'In Progress',       badge: 'primary',   icon: 'bi-arrow-repeat' },
  AWAITING_CUSTOMER: { label: 'Awaiting Customer', badge: 'info',      icon: 'bi-chat-left-dots' },
  RESOLVED:          { label: 'Resolved',          badge: 'success',   icon: 'bi-check2-circle' },
  CLOSED:            { label: 'Closed',            badge: 'secondary', icon: 'bi-archive' },
};

const CATEGORIES = [
  'Settlement status enquiry',
  'Payment not received',
  'Onboarding / access',
  'Document or receipt request',
  'Data discrepancy',
  'Platform defect',
];

// Categories offered in the "New Ticket" intake form. "Settlement request" is
// the deep-link target used by the Manage Your Ledger dashboard.
const FORM_CATEGORIES = ['Settlement request', ...CATEGORIES];

const INCIDENTS = [
  { id: 'INC-2607-0148', at: '2026-07-28T13:42:00Z', network: 'institutional', severity: 'P1', status: 'IN_PROGRESS',       category: 'Settlement status enquiry', subject: 'Close-out receipt not visible to counterparty', requester: 'Tradeweb Markets · ops desk',            owner: 'a.clayborne@krotrust.org', firstResponseMins: 9,  updates: 4, detail: 'Counterparty cannot see the close-out receipt for a settled draw. Receipt confirmed present on ledger; investigating the counterparty-side registry read.' },
  { id: 'INC-2607-0147', at: '2026-07-28T11:15:00Z', network: 'supplier',      severity: 'P2', status: 'TRIAGED',           category: 'Payment not received',      subject: 'Wire not credited — African Supplier Network',  requester: 'African Supplier Network · finance',       owner: 'a.clayborne@krotrust.org', firstResponseMins: 34, updates: 2, detail: 'Wire instructed 2026-07-27. Column N.A. shows the instruction accepted; tracing with the beneficiary bank.' },
  { id: 'INC-2607-0146', at: '2026-07-28T09:03:00Z', network: 'distribution',  severity: 'P3', status: 'AWAITING_CUSTOMER', category: 'Data discrepancy',          subject: 'Impact metrics differ from partner report',     requester: '1687 Productions · reporting',            owner: 'l.nakamura@krotrust.org',  firstResponseMins: 52, updates: 3, detail: 'Partner-side figures use a different reporting period. Requested their period definition to align the comparison.' },
  { id: 'INC-2607-0145', at: '2026-07-27T16:28:00Z', network: 'distribution',  severity: 'P2', status: 'RESOLVED',          category: 'Onboarding / access',       subject: 'Portal access for two new users',               requester: 'Sexi Cycle · admin',                      owner: 'l.nakamura@krotrust.org',  firstResponseMins: 21, updates: 5, detail: 'Access provisioned for two users after verification. Confirmed working by the requester.' },
  { id: 'INC-2607-0144', at: '2026-07-27T14:05:00Z', network: 'institutional', severity: 'P2', status: 'RESOLVED',          category: 'Document or receipt request', subject: 'Document Key re-issue request',               requester: 'Cumberland DRW · settlement',             owner: 'a.clayborne@krotrust.org', firstResponseMins: 12, updates: 3, detail: 'Document Key re-issued to the registered contact after identity verification.' },
  { id: 'INC-2607-0143', at: '2026-07-27T10:44:00Z', network: 'supplier',      severity: 'P4', status: 'CLOSED',            category: 'Platform defect',           subject: 'Dashboard column sort not persisting',          requester: 'So Fraiche · operations',                 owner: 'l.nakamura@krotrust.org',  firstResponseMins: 96, updates: 2, detail: 'Reproduced and logged as a low-priority UI defect. Requester accepted the workaround.' },
  { id: 'INC-2607-0142', at: '2026-07-26T15:19:00Z', network: 'institutional', severity: 'P3', status: 'CLOSED',            category: 'Settlement status enquiry', subject: 'Confirmation of repo interest calculation',     requester: 'Five North Capital · middle office',       owner: 'a.clayborne@krotrust.org', firstResponseMins: 41, updates: 2, detail: 'Walked through the ACT/360 day-count calculation against the settled draw. Figures agreed.' },
  { id: 'INC-2607-0141', at: '2026-07-26T08:52:00Z', network: 'distribution',  severity: 'P1', status: 'CLOSED',            category: 'Payment not received',      subject: 'ACH return on scheduled payment',               requester: 'AI for Job Security Foundation · finance', owner: 'a.clayborne@krotrust.org', firstResponseMins: 7,  updates: 6, detail: 'ACH returned R01 by the receiving bank. Payment re-issued after account details were re-verified.' },
];

// Rolling 7-day incident volume per network, oldest first.
const VOLUME_TREND = [
  { day: 'Jul 22', institutional: 2, distribution: 3, supplier: 1 },
  { day: 'Jul 23', institutional: 1, distribution: 2, supplier: 2 },
  { day: 'Jul 24', institutional: 3, distribution: 1, supplier: 1 },
  { day: 'Jul 25', institutional: 2, distribution: 2, supplier: 3 },
  { day: 'Jul 26', institutional: 2, distribution: 1, supplier: 1 },
  { day: 'Jul 27', institutional: 1, distribution: 1, supplier: 1 },
  { day: 'Jul 28', institutional: 1, distribution: 1, supplier: 1 },
];

const PLUG_AND_PLAY = [
  { icon: 'bi-plug-fill',        title: 'No custom integration', desc: 'Mounts onto any KRO Exchange network using the shared incident taxonomy — nothing bespoke to build per participant.' },
  { icon: 'bi-arrow-left-right', title: 'One source of record',  desc: `Studio and the Marketplace page render the same queues, so participants and internal responders see identical state.` },
  { icon: 'bi-stopwatch',        title: 'SLA presets',           desc: 'P1–P4 response and resolution targets ship with the app and apply consistently across every network.' },
  { icon: 'bi-people',           title: 'Shared responder pool', desc: 'One queue across all networks means incidents route to whoever is on shift, not to a network-specific silo.' },
];

/* ─────────────────────────── Helpers ─────────────────────────── */

const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
const networkName = (id) => EXCHANGE_NETWORKS.find(n => n.id === id)?.name || id;
const severityMeta = (id) => SEVERITIES.find(s => s.id === id);

const isActive = (s) => ['OPEN', 'TRIAGED', 'IN_PROGRESS', 'AWAITING_CUSTOMER'].includes(s);

const fmtMins = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);

/* ─────────────────────────── Component ─────────────────────────── */

const CustomerServicePortal = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [networkFilter, setNetworkFilter] = useState('ALL');
  const [severityFilter, setSeverityFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const [expandedId, setExpandedId] = useState(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // New-ticket intake form (settlement request deep-link target).
  const [ticket, setTicket] = useState({
    network: 'institutional',
    severity: 'P2',
    category: 'Settlement request',
    subject: '',
    reference: '',
    details: '',
  });
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => { document.title = 'Customer Service Portal — KRO Exchange'; }, []);

  // Deep-link: /kro-exchange/customer-service-portal?new=1&type=settlement&ref=<denom>
  useEffect(() => {
    const isNew = searchParams.get('new');
    const type = searchParams.get('type');
    const ref = searchParams.get('ref') || '';
    if (!isNew && !type && !ref) return;
    setActiveTab('new');
    setTicket((t) => ({
      ...t,
      category: type === 'settlement' ? 'Settlement request' : t.category,
      subject: t.subject || (type === 'settlement' ? 'Settlement request' : t.subject),
      reference: ref || t.reference,
    }));
  }, [searchParams]);

  const submitTicket = (e) => {
    e.preventDefault();
    const stamp = new Date().toISOString().slice(2, 10).replace(/-/g, '');
    const id = `INC-${stamp}-${Math.floor(1000 + Math.random() * 9000)}`;
    setSubmitted({ id, ...ticket });
  };

  const resetTicketForm = () => {
    setSubmitted(null);
    setTicket({ network: 'institutional', severity: 'P2', category: 'Settlement request', subject: '', reference: '', details: '' });
  };

  const active = INCIDENTS.filter(i => isActive(i.status));

  const stats = useMemo(() => {
    const withinResponse = INCIDENTS.filter(i => i.firstResponseMins <= (severityMeta(i.severity)?.respondMins ?? 0));
    const p1 = INCIDENTS.filter(i => i.severity === 'P1');
    const resolved = INCIDENTS.filter(i => ['RESOLVED', 'CLOSED'].includes(i.status));
    const avgFirstResponse = Math.round(INCIDENTS.reduce((s, i) => s + i.firstResponseMins, 0) / INCIDENTS.length);
    return {
      activeCount: active.length,
      p1Active: p1.filter(i => isActive(i.status)).length,
      slaPct: Math.round((withinResponse.length / INCIDENTS.length) * 100),
      avgFirstResponse,
      resolvedCount: resolved.length,
      totalCount: INCIDENTS.length,
    };
  }, [active.length]);

  const filtered = useMemo(() => INCIDENTS.filter(i =>
    (networkFilter === 'ALL' || i.network === networkFilter) &&
    (severityFilter === 'ALL' || i.severity === severityFilter) &&
    (statusFilter === 'ALL' || (statusFilter === 'ACTIVE' ? isActive(i.status) : i.status === statusFilter))
  ), [networkFilter, severityFilter, statusFilter]);

  const byNetwork = useMemo(() => EXCHANGE_NETWORKS.map(n => {
    const all = INCIDENTS.filter(i => i.network === n.id);
    const open = all.filter(i => isActive(i.status));
    const avgResp = all.length ? Math.round(all.reduce((s, i) => s + i.firstResponseMins, 0) / all.length) : 0;
    return { ...n, total: all.length, open: open.length, avgResp };
  }), []);

  const maxVolume = Math.max(...VOLUME_TREND.map(d => d.institutional + d.distribution + d.supplier));

  /* ── Presentational helpers ──────────────────────────────────── */

  const KpiCard = ({ icon, label, value, sub, tone }) => (
    <div className="col-6 col-lg-3">
      <div className="card border-0 shadow-sm h-100">
        <div className="card-body">
          <div className="d-flex align-items-center gap-2 mb-1">
            <i className={`bi ${icon} text-${tone}`}></i>
            <span className="text-muted text-uppercase" style={{ fontSize: '0.68rem', letterSpacing: '0.6px', fontWeight: 700 }}>{label}</span>
          </div>
          <div className="fw-bold" style={{ fontSize: '1.5rem' }}>{value}</div>
          <small className="text-muted">{sub}</small>
        </div>
      </div>
    </div>
  );

  const IncidentTable = ({ rows }) => (
    <div className="table-responsive">
      <table className="table table-hover align-middle mb-0">
        <thead className="table-light">
          <tr style={{ fontSize: '0.76rem' }}>
            <th className="ps-3">Incident</th>
            <th>Network</th>
            <th>Severity</th>
            <th>Subject</th>
            <th>Owner</th>
            <th>Status</th>
            <th className="text-end pe-3">Detail</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(i => {
            const sev = severityMeta(i.severity);
            const onTime = i.firstResponseMins <= (sev?.respondMins ?? 0);
            return (
              <React.Fragment key={i.id}>
                <tr style={{ fontSize: '0.8rem' }}>
                  <td className="ps-3">
                    <code style={{ fontSize: '0.7rem' }}>{i.id}</code>
                    <div><small className="text-muted">{fmtDateTime(i.at)}</small></div>
                  </td>
                  <td><span className="badge bg-light text-dark border" style={{ fontSize: '0.66rem' }}>{networkName(i.network)}</span></td>
                  <td><span className={`badge bg-${sev?.tone}`} style={{ fontSize: '0.64rem' }}>{i.severity}</span></td>
                  <td>
                    <div className="fw-semibold">{i.subject}</div>
                    <small className="text-muted">{i.category} · {i.requester}</small>
                  </td>
                  <td><small>{i.owner}</small></td>
                  <td>
                    <span className={`badge bg-${STATUS_META[i.status].badge}`} style={{ fontSize: '0.66rem' }}>
                      <i className={`bi ${STATUS_META[i.status].icon} me-1`}></i>{STATUS_META[i.status].label}
                    </span>
                  </td>
                  <td className="text-end pe-3">
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setExpandedId(expandedId === i.id ? null : i.id)}>
                      <i className={`bi ${expandedId === i.id ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                    </button>
                  </td>
                </tr>
                {expandedId === i.id && (
                  <tr className="bg-light">
                    <td colSpan={7} className="px-3 py-3">
                      <div className="row g-3" style={{ fontSize: '0.8rem' }}>
                        <div className="col-12 col-lg-7">
                          <div className="fw-bold mb-1" style={{ fontSize: '0.76rem' }}>RESPONSE NOTES</div>
                          <p className="text-muted mb-0">{i.detail}</p>
                        </div>
                        <div className="col-12 col-lg-5">
                          <div className="fw-bold mb-1" style={{ fontSize: '0.76rem' }}>SLA</div>
                          <div className="d-flex justify-content-between py-1 border-bottom">
                            <span className="text-muted">Target first response</span>
                            <span className="fw-semibold">{fmtMins(sev?.respondMins ?? 0)}</span>
                          </div>
                          <div className="d-flex justify-content-between py-1 border-bottom">
                            <span className="text-muted">Actual first response</span>
                            <span className={`fw-semibold ${onTime ? 'text-success' : 'text-danger'}`}>
                              {fmtMins(i.firstResponseMins)} {onTime ? '· within target' : '· breached'}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between py-1 border-bottom">
                            <span className="text-muted">Target resolution</span>
                            <span className="fw-semibold">{sev?.resolveHrs}h</span>
                          </div>
                          <div className="d-flex justify-content-between py-1">
                            <span className="text-muted">Updates posted</span>
                            <span className="fw-semibold">{i.updates}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  /* ─────────────────────────── Render ─────────────────────────── */

  return (
    <div className="container-fluid py-4">

      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item"><Link to="/kro-exchange/settlement-hub">KRO Exchange</Link></li>
          <li className="breadcrumb-item active" aria-current="page">Customer Service Portal</li>
        </ol>
      </nav>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <h1 className="display-6 fw-bold mb-0">
              <i className="bi bi-headset me-2 text-primary"></i>Customer Service Portal
            </h1>
            <span className="badge bg-primary" style={{ fontSize: '0.68rem' }}>
              <i className="bi bi-star-fill me-1"></i>FEATURED APP
            </span>
            <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25" style={{ fontSize: '0.68rem' }}>
              <i className="bi bi-plug-fill me-1"></i>PLUG &amp; PLAY
            </span>
          </div>
          <p className="text-muted mb-0" style={{ fontSize: '0.88rem' }}>
            Incident response management across all KRO Exchange networks — one queue, one taxonomy, one set of SLA targets.
          </p>
        </div>
        <a href={MARKETPLACE_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
          <i className="bi bi-box-arrow-up-right me-1"></i>Open on KRO Marketplace
        </a>
      </div>

      {/* Source-of-record notice */}
      <div className="alert alert-light border py-2 mb-4" style={{ fontSize: '0.8rem' }}>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="rounded-circle d-inline-block" style={{ width: 8, height: 8, background: '#198754', boxShadow: '0 0 5px #198754' }} />
          <strong>Connected to the Marketplace service desk</strong>
          <span className="text-muted">·</span>
          <span className="text-muted">
            Source of record: <a href={MARKETPLACE_URL} target="_blank" rel="noopener noreferrer">{MARKETPLACE_URL.replace('https://', '')}</a>
          </span>
          <span className="text-muted">·</span>
          <span className="text-muted">Studio renders the same dashboards participants see on the customer-facing page.</span>
        </div>
      </div>

      {/* KPI strip */}
      <div className="row g-3 mb-4">
        <KpiCard icon="bi-record-circle"  label="Active Incidents" tone={stats.activeCount ? 'danger' : 'success'} value={stats.activeCount} sub={`${stats.p1Active} at P1 critical`} />
        <KpiCard icon="bi-stopwatch"      label="Avg First Response" tone="primary" value={fmtMins(stats.avgFirstResponse)} sub="Across all severities" />
        <KpiCard icon="bi-speedometer2"   label="SLA Attainment"   tone={stats.slaPct >= 95 ? 'success' : 'warning'} value={`${stats.slaPct}%`} sub="First response within target" />
        <KpiCard icon="bi-check2-circle"  label="Resolved"         tone="success" value={`${stats.resolvedCount} / ${stats.totalCount}`} sub="Rolling 7-day window" />
      </div>

      {/* Tabs */}
      <ul className="nav nav-pills mb-4" style={{ gap: '0.25rem' }}>
        {[
          { id: 'new',       icon: 'bi-plus-circle',     label: 'New Ticket' },
          { id: 'dashboard', icon: 'bi-speedometer2',    label: 'Incident Dashboard' },
          { id: 'queue',     icon: 'bi-inbox',           label: 'Response Queue', count: stats.activeCount },
          { id: 'sla',       icon: 'bi-stopwatch',       label: 'SLA & Performance' },
          { id: 'coverage',  icon: 'bi-diagram-3',       label: 'Network Coverage' },
        ].map(t => (
          <li key={t.id} className="nav-item">
            <button className={`nav-link ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
              <i className={`bi ${t.icon} me-1`}></i>{t.label}
              {t.count > 0 && <span className="badge bg-white text-dark ms-2" style={{ fontSize: '0.62rem' }}>{t.count}</span>}
            </button>
          </li>
        ))}
      </ul>

      {/* ══════════════════ TAB: NEW TICKET ══════════════════ */}
      {activeTab === 'new' && (
        <div className="row g-4">
          <div className="col-12 col-xl-8">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom py-3">
                <h6 className="mb-0 fw-bold"><i className="bi bi-ticket-detailed me-2 text-primary"></i>Open a Ticket</h6>
                <small className="text-muted">Settlement requests are validated against your Trust Ledger, then routed through the Exchange for processing.</small>
              </div>
              <div className="card-body">
                {submitted ? (
                  <div className="text-center py-4">
                    <i className="bi bi-check2-circle text-success d-block mb-2" style={{ fontSize: '2.4rem' }}></i>
                    <h5 className="fw-bold mb-1">Ticket submitted</h5>
                    <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
                      Reference <code>{submitted.id}</code> · {submitted.category}
                    </p>
                    <div className="alert alert-light border text-start mx-auto" style={{ maxWidth: 520, fontSize: '0.82rem' }}>
                      <div className="d-flex justify-content-between py-1 border-bottom"><span className="text-muted">Network</span><span className="fw-semibold">{networkName(submitted.network)}</span></div>
                      <div className="d-flex justify-content-between py-1 border-bottom"><span className="text-muted">Severity</span><span className="fw-semibold">{severityMeta(submitted.severity)?.label}</span></div>
                      {submitted.reference && <div className="d-flex justify-content-between py-1 border-bottom"><span className="text-muted">Collateral reference</span><span className="fw-semibold">{submitted.reference}</span></div>}
                      <div className="d-flex justify-content-between py-1"><span className="text-muted">Routing</span><span className="fw-semibold">Exchange · settlement operations</span></div>
                    </div>
                    <div className="d-flex gap-2 justify-content-center mt-3">
                      <button className="btn btn-outline-secondary btn-sm" onClick={resetTicketForm}>
                        <i className="bi bi-plus-circle me-1"></i>Open another
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={() => navigate('/kro-exchange')}>
                        Go to Exchange →
                      </button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={submitTicket}>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label fw-semibold" style={{ fontSize: '0.8rem' }}>Category</label>
                        <select className="form-select" value={ticket.category} onChange={(e) => setTicket({ ...ticket, category: e.target.value })}>
                          {FORM_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold" style={{ fontSize: '0.8rem' }}>Exchange network</label>
                        <select className="form-select" value={ticket.network} onChange={(e) => setTicket({ ...ticket, network: e.target.value })}>
                          {EXCHANGE_NETWORKS.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold" style={{ fontSize: '0.8rem' }}>Severity</label>
                        <select className="form-select" value={ticket.severity} onChange={(e) => setTicket({ ...ticket, severity: e.target.value })}>
                          {SEVERITIES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold" style={{ fontSize: '0.8rem' }}>Collateral reference <span className="text-muted fw-normal">(optional)</span></label>
                        <input type="text" className="form-control" placeholder="e.g. kro.as.accountability.v2" value={ticket.reference} onChange={(e) => setTicket({ ...ticket, reference: e.target.value })} />
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-semibold" style={{ fontSize: '0.8rem' }}>Subject</label>
                        <input type="text" className="form-control" required placeholder="Short summary of your request" value={ticket.subject} onChange={(e) => setTicket({ ...ticket, subject: e.target.value })} />
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-semibold" style={{ fontSize: '0.8rem' }}>Details</label>
                        <textarea className="form-control" rows={4} placeholder="Describe the settlement request or issue…" value={ticket.details} onChange={(e) => setTicket({ ...ticket, details: e.target.value })} />
                      </div>
                    </div>
                    <div className="d-flex gap-2 mt-3">
                      <button type="submit" className="btn btn-primary">
                        <i className="bi bi-send me-1"></i>Submit ticket
                      </button>
                      <button type="button" className="btn btn-outline-secondary" onClick={() => setActiveTab('dashboard')}>
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>

          <div className="col-12 col-xl-4">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom py-3">
                <h6 className="mb-0 fw-bold"><i className="bi bi-arrow-left-right me-2 text-success"></i>Settlement Request Flow</h6>
              </div>
              <div className="card-body">
                <ol className="mb-0 ps-3" style={{ fontSize: '0.83rem' }}>
                  <li className="mb-2">Open a ticket with your collateral reference.</li>
                  <li className="mb-2">KRO stewards validate the request against your Trust Ledger.</li>
                  <li className="mb-0">Approved requests route through the Exchange for settlement.</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════ TAB: INCIDENT DASHBOARD ══════════════════ */}
      {activeTab === 'dashboard' && (
        <div className="row g-4">
          <div className="col-12 col-xl-8">
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white border-bottom py-3">
                <h6 className="mb-0 fw-bold"><i className="bi bi-record-circle me-2 text-danger"></i>Active Incidents</h6>
                <small className="text-muted">Everything currently open across the three exchange networks.</small>
              </div>
              <div className="card-body p-0">
                {active.length === 0
                  ? <div className="text-center text-muted py-5"><i className="bi bi-check2-all d-block mb-2 text-success" style={{ fontSize: '1.8rem' }}></i>No active incidents.</div>
                  : <IncidentTable rows={active} />}
              </div>
            </div>

            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom py-3">
                <h6 className="mb-0 fw-bold"><i className="bi bi-bar-chart me-2 text-primary"></i>Volume — Rolling 7 Days</h6>
              </div>
              <div className="card-body">
                <div className="d-flex align-items-end justify-content-between gap-2" style={{ height: 160 }}>
                  {VOLUME_TREND.map(d => {
                    const total = d.institutional + d.distribution + d.supplier;
                    const h = (total / maxVolume) * 130;
                    return (
                      <div key={d.day} className="d-flex flex-column align-items-center flex-grow-1">
                        <small className="text-muted mb-1" style={{ fontSize: '0.68rem' }}>{total}</small>
                        <div className="w-100 d-flex flex-column justify-content-end" style={{ height: h, maxWidth: 48 }}>
                          <div style={{ height: `${(d.institutional / total) * 100}%`, background: '#0d6efd' }} title={`Institutional: ${d.institutional}`} />
                          <div style={{ height: `${(d.distribution / total) * 100}%`, background: '#0dcaf0' }} title={`Distribution: ${d.distribution}`} />
                          <div style={{ height: `${(d.supplier / total) * 100}%`, background: '#198754', borderRadius: '0 0 3px 3px' }} title={`Supplier: ${d.supplier}`} />
                        </div>
                        <small className="text-muted mt-2" style={{ fontSize: '0.66rem' }}>{d.day}</small>
                      </div>
                    );
                  })}
                </div>
                <div className="d-flex gap-3 justify-content-center mt-3" style={{ fontSize: '0.74rem' }}>
                  {[{ c: '#0d6efd', l: 'Institutional Exchange' }, { c: '#0dcaf0', l: 'Distribution Channel' }, { c: '#198754', l: 'Supplier Network' }].map(x => (
                    <span key={x.l} className="d-inline-flex align-items-center gap-1">
                      <span style={{ width: 10, height: 10, background: x.c, display: 'inline-block', borderRadius: 2 }} />
                      <span className="text-muted">{x.l}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-xl-4">
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white border-bottom py-3">
                <h6 className="mb-0 fw-bold"><i className="bi bi-plug-fill me-2 text-success"></i>Why Plug &amp; Play</h6>
              </div>
              <div className="card-body">
                {PLUG_AND_PLAY.map(f => (
                  <div key={f.title} className="d-flex gap-3 mb-3">
                    <i className={`bi ${f.icon} text-success flex-shrink-0`} style={{ fontSize: '1.05rem' }}></i>
                    <div>
                      <div className="fw-semibold" style={{ fontSize: '0.83rem' }}>{f.title}</div>
                      <div className="text-muted" style={{ fontSize: '0.78rem' }}>{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom py-3">
                <h6 className="mb-0 fw-bold"><i className="bi bi-tags me-2 text-primary"></i>Incident Categories</h6>
              </div>
              <div className="card-body p-0">
                <table className="table table-sm align-middle mb-0" style={{ fontSize: '0.78rem' }}>
                  <tbody>
                    {CATEGORIES.map(c => {
                      const n = INCIDENTS.filter(i => i.category === c).length;
                      return (
                        <tr key={c}>
                          <td className="ps-3">{c}</td>
                          <td className="text-end pe-3 fw-semibold">{n}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════ TAB: RESPONSE QUEUE ══════════════════ */}
      {activeTab === 'queue' && (
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white border-bottom py-3">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h6 className="mb-0 fw-bold"><i className="bi bi-inbox me-2 text-primary"></i>Response Queue</h6>
              <div className="d-flex gap-2 flex-wrap">
                <select className="form-select form-select-sm" style={{ width: 190 }} value={networkFilter} onChange={e => setNetworkFilter(e.target.value)}>
                  <option value="ALL">All networks</option>
                  {EXCHANGE_NETWORKS.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
                </select>
                <select className="form-select form-select-sm" style={{ width: 150 }} value={severityFilter} onChange={e => setSeverityFilter(e.target.value)}>
                  <option value="ALL">All severities</option>
                  {SEVERITIES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
                <select className="form-select form-select-sm" style={{ width: 175 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                  <option value="ACTIVE">Active only</option>
                  <option value="ALL">All statuses</option>
                  {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            {filtered.length === 0
              ? <div className="text-center text-muted py-5"><i className="bi bi-inbox d-block mb-2" style={{ fontSize: '1.8rem' }}></i>No incidents match the current filters.</div>
              : <IncidentTable rows={filtered} />}
          </div>
          <div className="card-footer bg-light text-muted" style={{ fontSize: '0.74rem' }}>
            <i className="bi bi-info-circle me-1"></i>
            Showing {filtered.length} of {INCIDENTS.length} incidents. Participants raise and track the same incidents from the{' '}
            <a href={MARKETPLACE_URL} target="_blank" rel="noopener noreferrer">Marketplace service desk</a>.
          </div>
        </div>
      )}

      {/* ══════════════════ TAB: SLA & PERFORMANCE ══════════════════ */}
      {activeTab === 'sla' && (
        <div className="row g-4">
          <div className="col-12 col-xl-7">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-bottom py-3">
                <h6 className="mb-0 fw-bold"><i className="bi bi-stopwatch me-2 text-primary"></i>SLA Targets &amp; Attainment</h6>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead className="table-light">
                      <tr style={{ fontSize: '0.76rem' }}>
                        <th className="ps-3">Severity</th>
                        <th>Definition</th>
                        <th className="text-end">First response</th>
                        <th className="text-end">Resolution</th>
                        <th className="text-end pe-3">Attainment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {SEVERITIES.map(s => {
                        const rows = INCIDENTS.filter(i => i.severity === s.id);
                        const met = rows.filter(i => i.firstResponseMins <= s.respondMins).length;
                        const pct = rows.length ? Math.round((met / rows.length) * 100) : null;
                        return (
                          <tr key={s.id} style={{ fontSize: '0.8rem' }}>
                            <td className="ps-3"><span className={`badge bg-${s.tone}`} style={{ fontSize: '0.66rem' }}>{s.label}</span></td>
                            <td className="text-muted" style={{ fontSize: '0.77rem' }}>{s.desc}</td>
                            <td className="text-end fw-semibold">{fmtMins(s.respondMins)}</td>
                            <td className="text-end fw-semibold">{s.resolveHrs}h</td>
                            <td className="text-end pe-3">
                              {pct === null
                                ? <small className="text-muted">No incidents</small>
                                : <span className={`fw-bold ${pct >= 95 ? 'text-success' : pct >= 80 ? 'text-warning' : 'text-danger'}`}>{pct}%</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="card-footer bg-light text-muted" style={{ fontSize: '0.74rem' }}>
                <i className="bi bi-info-circle me-1"></i>
                Targets ship with the app and apply identically on every network, so attainment is comparable across participants.
              </div>
            </div>
          </div>

          <div className="col-12 col-xl-5">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-bottom py-3">
                <h6 className="mb-0 fw-bold"><i className="bi bi-graph-up me-2 text-primary"></i>Response Performance</h6>
              </div>
              <div className="card-body">
                {SEVERITIES.map(s => {
                  const rows = INCIDENTS.filter(i => i.severity === s.id);
                  if (!rows.length) return null;
                  const avg = Math.round(rows.reduce((a, i) => a + i.firstResponseMins, 0) / rows.length);
                  const pct = Math.min(100, Math.round((avg / s.respondMins) * 100));
                  return (
                    <div key={s.id} className="mb-3">
                      <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.79rem' }}>
                        <span className="fw-semibold">{s.label}</span>
                        <span className="text-muted">{fmtMins(avg)} avg vs {fmtMins(s.respondMins)} target</span>
                      </div>
                      <div className="progress" style={{ height: 7 }}>
                        <div className={`progress-bar bg-${pct <= 100 ? 'success' : 'danger'}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
                <div className="alert alert-light border py-2 mt-3 mb-0" style={{ fontSize: '0.77rem' }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Bars show average first response as a share of the target. Shorter is better; a full bar means the average is at the target.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════ TAB: NETWORK COVERAGE ══════════════════ */}
      {activeTab === 'coverage' && (
        <>
          <div className="row g-3 mb-4">
            {byNetwork.map(n => (
              <div key={n.id} className="col-12 col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white border-bottom py-3 d-flex align-items-center justify-content-between">
                    <h6 className="mb-0 fw-bold" style={{ fontSize: '0.9rem' }}>
                      <i className={`bi ${n.icon} me-2 text-${n.tone}`}></i>{n.name}
                    </h6>
                    <span className={`badge bg-${n.open ? 'danger' : 'success'}`} style={{ fontSize: '0.64rem' }}>
                      {n.open ? `${n.open} open` : 'Clear'}
                    </span>
                  </div>
                  <div className="card-body">
                    <div className="row text-center g-2 mb-3">
                      <div className="col-4">
                        <div className="fw-bold" style={{ fontSize: '1.25rem' }}>{n.total}</div>
                        <small className="text-muted" style={{ fontSize: '0.7rem' }}>Total</small>
                      </div>
                      <div className="col-4">
                        <div className="fw-bold" style={{ fontSize: '1.25rem' }}>{n.open}</div>
                        <small className="text-muted" style={{ fontSize: '0.7rem' }}>Active</small>
                      </div>
                      <div className="col-4">
                        <div className="fw-bold" style={{ fontSize: '1.25rem' }}>{fmtMins(n.avgResp)}</div>
                        <small className="text-muted" style={{ fontSize: '0.7rem' }}>Avg resp.</small>
                      </div>
                    </div>
                    <ul className="list-unstyled mb-0" style={{ fontSize: '0.78rem' }}>
                      {INCIDENTS.filter(i => i.network === n.id).slice(0, 3).map(i => (
                        <li key={i.id} className="d-flex justify-content-between py-1 border-bottom">
                          <span className="text-truncate me-2" style={{ maxWidth: '70%' }}>{i.subject}</span>
                          <span className={`badge bg-${severityMeta(i.severity)?.tone}`} style={{ fontSize: '0.6rem' }}>{i.severity}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom py-3">
              <h6 className="mb-0 fw-bold"><i className="bi bi-diagram-3 me-2 text-primary"></i>Coverage Model</h6>
            </div>
            <div className="card-body">
              <p className="text-muted mb-3" style={{ fontSize: '0.83rem' }}>
                One responder pool covers all three KRO Exchange networks. Incidents are triaged by severity rather than by network,
                so a P1 on the Supplier Network is picked up with the same urgency as a P1 on the Institutional Exchange.
              </p>
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0" style={{ fontSize: '0.79rem' }}>
                  <thead className="table-light">
                    <tr style={{ fontSize: '0.74rem' }}>
                      <th className="ps-3">Network</th>
                      <th>Typical incident types</th>
                      <th className="pe-3">Escalation path</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="ps-3 fw-semibold">Institutional Exchange</td>
                      <td className="text-muted">Settlement status, receipt and document requests, repo interest queries</td>
                      <td className="text-muted pe-3">Responder → settlement operations → Settlement Hub exception monitor</td>
                    </tr>
                    <tr>
                      <td className="ps-3 fw-semibold">Distribution Channel</td>
                      <td className="text-muted">Payment status, access and onboarding, reporting discrepancies</td>
                      <td className="text-muted pe-3">Responder → payment operations → Payment Workflow exception queue</td>
                    </tr>
                    <tr>
                      <td className="ps-3 fw-semibold">Supplier Network</td>
                      <td className="text-muted">Payment status, supplier onboarding, platform defects</td>
                      <td className="text-muted pe-3">Responder → supplier operations → Ethics supplier ecosystem</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerServicePortal;
